import {Component, effect, inject} from '@angular/core';
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {LineChartModule} from "@swimlane/ngx-charts";
import {NgClass} from "@angular/common";
import {FixedSamplePoint, SynchronizationStatus} from "src/app/infrastructure/moovement-api/models/asset";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {ActionType, DataType} from "src/app/domain/domain-models";
import {CustomizableAssetComponent} from "src/app/presentation/common/customizable-asset.component";
import {BluetoothSvgComponent, Status} from "src/app/presentation/common/icons/bluetooth-svg/bluetooth-svg.component";
import {AggregatedDeviceStateStore} from 'src/app/domain/devices/aggregated-state/aggregated-device-state.store';
import {
  LatLong,
  SampleBasedLocationButton
} from "src/app/presentation/main-view/sample-based-location-button/sample-based-location-button.component";
import {MapStateService} from 'src/app/presentation/main-view/map-container/map-container-component';
import {PaddockStore} from 'src/app/domain/assets/paddocks/paddock.store';
import {FixedSamplePointsStore} from 'src/app/domain/assets/fixed-sample-points/fixed-sample-points.store';
import {AnimalsStore} from 'src/app/domain/assets/animals/animals.store';
import {InternalNotificationType} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {SwitchComponent} from "src/app/presentation/common/switch/switch.component";
import {parseOptionalFloat} from 'src/app/domain/util/parsing/parsing';

@Component({
  selector: 'app-customize-proximity-sample-point',
  imports: [
    GenericButtonComponent,
    LineChartModule,
    NgClass,
    BluetoothSvgComponent,
    SampleBasedLocationButton,
    SwitchComponent
],
  templateUrl: './customize-proximity-sample-point.component.html',
  styleUrl: './customize-proximity-sample-point.component.css'
})
export class CustomizeProximitySamplePointComponent extends CustomizableAssetComponent<FixedSamplePoint> {

  aggregatedDeviceStateStore = inject(AggregatedDeviceStateStore);
  animalsStore = inject(AnimalsStore);
  paddockStore = inject(PaddockStore);

  Status = Status;

  private fixedSamplePointsStore = inject(FixedSamplePointsStore);

  constructor(
    dataSynchronisationService: DataSynchronisationService,
    dataSubscriptionService: DataSubscriptionService,
    private mapStateService: MapStateService,
    viewportService: ViewportService
  ) {
    super(dataSynchronisationService, dataSubscriptionService, viewportService)

    effect(() => {
      const assetId = this.id();
      if (!assetId) return;

      this.asset.set(this.fixedSamplePointsStore.entityMap()[assetId]);
    });
  }

  setName($event: any): void {
    this.asset.update(asset => {
      if (!asset) return undefined;

      return {
        ...asset,
        name: $event.target.value
      }
    });
  }

  toggleSampleBasedLocation() {
    this.asset.update(asset => {
      if (!asset) return undefined;

      return {
        ...asset,
        sampleBasedLocation: !asset.sampleBasedLocation
      }
    });
  }

  setLatitude($event: any): void {
    const asset = this.asset();
    if (!asset) return;

    this.updateLocation(parseOptionalFloat($event.target.value), asset.location!.coords![1]);
  }

  setLongitude($event: any) {
    const asset = this.asset();
    if (!asset) return;

    this.updateLocation(asset.location!.coords![0], parseOptionalFloat($event.target.value));
  }

  onLocationEmitted(latLong: LatLong) {
    this.updateLocation(latLong.latitude, latLong.longitude);
  }

  private updateLocation(latitude?: number, longitude?: number) {
    if (latitude === undefined || longitude === undefined) return;

    this.asset.update(asset => {
      if (!asset) return undefined;

      return {
        ...asset,
        location: {
          ...asset.location,
          coords: [latitude, longitude]
        }
      }
    });

    this.mapStateService.moveLocator(latitude, longitude);
    this.mapStateService.centerPan(latitude, longitude);
  }

  protected override confirmDropAPinMessage(): string {
      return "Are you sure you want to change the location of this BLE scanner?";
  }

  override async applyChanges() {
    if (this.loading || !this.canBeAltered) return;

    await this.dataSynchronisationService.applyAction(
      DataType.proximitySamplePoints,
      ActionType.mergeAsset,
      this.asset()!,
      SynchronizationStatus.syncPending
    );

    this.internalNotificationsStore.addNotification("Successfully altered BLE scanner.", InternalNotificationType.SUCCESS, false);

    this.router.navigate(['/main/proximity-sample-points']);
  }
}
